import { useCallback, useContext, useEffect, useState } from "react";
import {
  DEFAULT_INBOX_PAGE_NUMBER,
  DEFAULT_INBOX_PAGE_SIZE,
} from "../constants";
import { useApprovalsListProps } from "@/pages/inbox/hooks/types";
import { AuthContext } from "@/guards/auth/auth-context";
import { InboxItem } from "@/services/api/inbox/contracts";
import {
  requestApprovalsList,
  requestTrackingList,
  type InboxPayload,
} from "@/services/api/inbox/requests";

type InboxArrayWithTotal = InboxItem[] & { __totalCount?: number };

interface UseApprovalsListReturn {
  isLoading: boolean;
  data: InboxItem[];
  error?: unknown;
  handleLoadMoreData: () => void;
  hasMoreData: boolean;
  onResetData: () => void;
  onRemoveItem: (id: string) => void;
  totalCount?: number;
}

interface PayloadWithData {
  Data?: {
    LoaInbox?: InboxItem[];
    Count?: number;
  };
  Count?: number;
}

interface PayloadWithLoa {
  LoaInbox?: InboxItem[];
  Count?: number;
}

interface PayloadWithArray {
  data?: InboxItem[];
  Count?: number;
}

type PossiblePayload =
  | InboxItem[]
  | PayloadWithData
  | PayloadWithLoa
  | PayloadWithArray
  | Record<string, unknown>;

const isInboxArray = (p: unknown): p is InboxItem[] => Array.isArray(p);

const hasDataWithLoa = (p: unknown): p is PayloadWithData =>
  !!p &&
  typeof p === "object" &&
  Array.isArray((p as PayloadWithData).Data?.LoaInbox);

const hasLoaInbox = (p: unknown): p is PayloadWithLoa =>
  !!p && typeof p === "object" && Array.isArray((p as PayloadWithLoa).LoaInbox);

const hasDataArray = (p: unknown): p is PayloadWithArray =>
  !!p && typeof p === "object" && Array.isArray((p as PayloadWithArray).data);

export const useApprovalsList = ({
  tab,
  isTracking,
  searchQuery,
  isShowFullSearchList,
  appliedFilters,
}: useApprovalsListProps): UseApprovalsListReturn => {
  const { account } = useContext(AuthContext);

  const [state, setState] = useState({
    data: [] as InboxItem[],
    page: DEFAULT_INBOX_PAGE_NUMBER,
    hasMoreData: false,
    totalCount: undefined as number | undefined,
  });

  const [error, setError] = useState<unknown>();
  const [isLoading, setIsLoading] = useState(true);

  const { data, page } = state;

  useEffect(() => {
    setState({
      data: [],
      page: DEFAULT_INBOX_PAGE_NUMBER,
      hasMoreData: false,
      totalCount: undefined,
    });
  }, [tab, appliedFilters]);

  useEffect(() => {
    setIsLoading(true);

    const controller = new AbortController();
    const request = isTracking ? requestTrackingList : requestApprovalsList;

    const requestPayload: InboxPayload & { filterText?: string } = {
      email: account?.username ?? "",
      filters: appliedFilters,
      pageSize: DEFAULT_INBOX_PAGE_SIZE,
      pageNumber: page,
    };

    if (isShowFullSearchList) {
      requestPayload.filterText = String(searchQuery ?? "");
    }

    const getPayload = (resp: unknown): unknown => {
      if (
        resp &&
        typeof resp === "object" &&
        "data" in (resp as Record<string, unknown>)
      ) {
        return (resp as Record<string, unknown>).data;
      }

      return resp;
    };

    const parseCount = (v: unknown): number | undefined => {
      if (v === null || v === undefined) {
        return undefined;
      }

      const n = Number(v);

      return Number.isFinite(n) ? n : undefined;
    };

    const getCountFromPayload = (p: PossiblePayload): number | undefined => {
      if (!p || typeof p !== "object") {
        return undefined;
      }

      if ("Count" in p && p.Count !== undefined) {
        return parseCount((p as { Count?: unknown }).Count);
      }

      if (
        "Data" in p &&
        p.Data &&
        typeof p.Data === "object" &&
        "Count" in p.Data
      ) {
        return parseCount((p as PayloadWithData).Data?.Count);
      }

      return undefined;
    };

    request(requestPayload, { signal: controller.signal })
      .then(response => {
        const payload = getPayload(response);

        let items: InboxItem[] = [];
        let count: number | undefined;

        if (isInboxArray(payload)) {
          items = payload;
        } else if (hasDataWithLoa(payload)) {
          items = payload.Data!.LoaInbox ?? [];
          count = getCountFromPayload(payload);
        } else if (hasLoaInbox(payload)) {
          items = payload.LoaInbox ?? [];
          count = getCountFromPayload(payload);
        } else if (hasDataArray(payload)) {
          items = payload.data ?? [];
          count = getCountFromPayload(payload);
        } else {
          const possibleList =
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            (payload as Record<string, unknown>)?.Data?.data ??
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            (payload as Record<string, unknown>)?.data?.Data ??
            payload;

          if (hasDataWithLoa(possibleList)) {
            items = possibleList.Data!.LoaInbox ?? [];
            count = getCountFromPayload(payload as PossiblePayload);
          }
        }

        if (count === undefined) {
          count = getCountFromPayload(payload as PossiblePayload);
        }

        setState(prevState => {
          const merged: InboxItem[] =
            page === DEFAULT_INBOX_PAGE_NUMBER
              ? items
              : [...prevState.data, ...items];

          let newTotal: number | undefined = prevState.totalCount;

          if (typeof count === "number") {
            if (merged.length > count) {
              if (typeof prevState.totalCount === "number") {
                newTotal = prevState.totalCount;
              } else {
                newTotal = count;
              }
            } else {
              newTotal = count;
            }
          }

          if (typeof newTotal === "number") {
            (merged as InboxArrayWithTotal).__totalCount = newTotal;
          }

          return {
            ...prevState,
            data: merged,
            hasMoreData: items.length === DEFAULT_INBOX_PAGE_SIZE,
            totalCount: newTotal,
          };
        });
      })
      .catch(err => {
        setError(err);
        setState(previous => ({
          ...previous,
          hasMoreData: false,
        }));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedFilters,
    isTracking,
    account?.username,
    page,
    isShowFullSearchList,
    searchQuery,
  ]);

  const handleLoadMoreData = (): void => {
    if (!isLoading && !searchQuery?.length) {
      setState(previous => ({
        ...previous,
        page: previous.page + 1,
      }));
    }
  };

  const handleResetData = (): void => {
    setState({
      data: [],
      page: DEFAULT_INBOX_PAGE_NUMBER,
      hasMoreData: false,
      totalCount: undefined,
    });
  };

  const handleRemoveItem = useCallback((id: string): void => {
    setState(prev => ({
      ...prev,
      data: prev.data.filter(item => item.ReferenceNumber !== id),
    }));
  }, []);

  return {
    isLoading,
    data,
    error,
    handleLoadMoreData,
    hasMoreData: state.hasMoreData,
    onResetData: handleResetData,
    onRemoveItem: handleRemoveItem,
    totalCount: state.totalCount,
  };
};
